import React, { useState } from 'react';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', org: '', role: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong.');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Failed to send. Please try again.');
    }
  };

  return (
    <div className="cy-body">
      <div className="contact-page">
        <div className="contact-left">
          <span className="section-label">Contact us</span>
          <h1 className="contact-h1">
            See what you're <span>owed</span>.
          </h1>
          <p className="contact-intro">
            Upload one VBC contract in your demo and we'll show you exactly how much revenue you're leaving on the table — and the pathway to capture it.
          </p>

          <div className="contact-features">
            {[
              ['Under 60 seconds', 'From contract upload to full payout calculation.'],
              ['No commitment', 'A free, no-pressure walkthrough of the platform.'],
              ['Your data, your contract', 'We use your actual VBC contract in the demo.'],
            ].map(([title, desc]) => (
              <div key={title} className="contact-feature">
                <div className="cf-dot" />
                <div>
                  <div className="cf-title">{title}</div>
                  <div className="cf-desc">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="contact-right">
          {status === 'success' ? (
            <div className="contact-success">
              <div className="cs-icon">
                <CheckCircle size={32} />
              </div>
              <h2>Request received</h2>
              <p>
                Thanks, {form.name.split(' ')[0]}! We'll reach out to{' '}
                <strong>{form.email}</strong> within one business day to schedule your demo.
              </p>
              <Link to="/" className="btn-primary" style={{ marginTop: 24, display: 'inline-flex' }}>
                Back to home
              </Link>
            </div>
          ) : (
            <div className="contact-card">
              <div className="contact-card-header">
                <div className="contact-card-title">Request a demo</div>
                <p className="contact-card-sub">
                  Tell us a bit about yourself and we'll get in touch to schedule a walkthrough.
                </p>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Full name *</label>
                    <input
                      className="cf-input"
                      name="name"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Work email *</label>
                    <input
                      className="cf-input"
                      name="email"
                      type="email"
                      placeholder="jane@healthsystem.org"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="cf-row">
                  <div className="cf-field">
                    <label className="cf-label">Organization</label>
                    <input
                      className="cf-input"
                      name="org"
                      placeholder="Health system / ACO / FQHC"
                      value={form.org}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="cf-field">
                    <label className="cf-label">Role</label>
                    <select
                      className="cf-input cf-select"
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                    >
                      <option value="">Select role</option>
                      <option>CFO / Finance</option>
                      <option>Chief Medical Officer</option>
                      <option>VP of Population Health</option>
                      <option>Revenue Cycle Director</option>
                      <option>IT / Data</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="cf-field">
                  <label className="cf-label">Anything you'd like us to know?</label>
                  <textarea
                    className="cf-input cf-textarea"
                    name="message"
                    placeholder="Number of VBC contracts, payers, attributed lives, current challenges..."
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                {status === 'error' && (
                  <div className="cf-error">
                    <AlertCircle size={15} />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="cf-submit"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <span>Sending request…</span>
                  ) : (
                    <>
                      Submit request
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>

                <p className="cf-privacy">
                  We respect your privacy. Your information is only used to schedule your demo.
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
